import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Descriptions,
  Table,
  Tag,
  Button,
  Space,
  Typography,
  Card,
  InputNumber,
  Input,
  message,
  Spin,
  Row,
  Col,
  Divider,
  Tooltip,
  Alert,
  Modal,
  Select,
  Radio,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  InboxOutlined,
  SaveOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  StopOutlined,
  PrinterOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getPO, updatePOLines, updatePOStatus, createGRN, costCenterLabel, catalogPricesBatch, requestPOLineCancel, decidePOLineCancel } from '../../api';
import type { ItemPrices, POLineMrAction } from '../../api';
import { useAuth } from '../../context/AuthContext';
import EntityHistory from '../../components/EntityHistory';
import ConfigurableTable from '../../components/ConfigurableTable';
import {
  ItemCell,
  MoneyCell,
  StatusPill,
  ProgressCell,
  QtySplitCell,
  DensityToggle,
  useDensity,
} from '../../components/table-cells';
import { useItemPictures } from '../../hooks/useItemPictures';

const { Title, Text } = Typography;

/** A line with no catalog code: a stated gap where the code chip would sit. */
const freeTextMark = (
  <span
    style={{
      display: 'inline-block',
      padding: '0 6px',
      fontSize: 11,
      lineHeight: '18px',
      color: '#8a94a6',
      border: '1px dashed #c9cede',
      borderRadius: 4,
      whiteSpace: 'nowrap',
    }}
  >
    free text
  </span>
);

const STATUS_TAG_COLORS: Record<string, string> = {
  Open: 'blue',
  Ordered: 'purple',
  'Partially Received': 'cyan',
  Received: 'green',
  Closed: 'default',
  Cancelled: 'red',
  'Pending Cancellation': 'orange',
};

const POView: React.FC = () => {
  const { poId } = useParams<{ poId: string }>();
  const navigate = useNavigate();
  const { isProcurement, isAdmin, isApprover, canAccess } = useAuth();
  const [loading, setLoading] = useState(true);
  const [po, setPo] = useState<any>(null);
  const [receiving, setReceiving] = useState(false);
  const [grnModalOpen, setGrnModalOpen] = useState(false);
  const [grnForm, setGrnForm] = useState<{
    deliveryNoteRef: string;
    supplierInvoice: string;
    notes: string;
    lines: Record<number, {
      deliveredQty: number;
      acceptedQty: number;
      rejectedQty: number;
      rejectReason: string;
      inspectionNotes: string;
    }>;
  }>({ deliveryNoteRef: '', supplierInvoice: '', notes: '', lines: {} });
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Record<number, { partNumber?: string; actualUnitPrice?: number }>>({});
  // Price engine: list / last paid per catalog item for the edit-mode hints
  const [priceRef, setPriceRef] = useState<Record<number, ItemPrices>>({});
  const [saving, setSaving] = useState(false);
  const [statusChanging, setStatusChanging] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // Whole-PO cancel runs through the line engine (2026-09-16): one MR outcome for every open line.
  const [cancelMrAction, setCancelMrAction] = useState<POLineMrAction | null>(null);
  const [poDecisionMrAction, setPoDecisionMrAction] = useState<POLineMrAction | null>(null);
  const [density, setDensity] = useDensity('po-view-lines');
  const compact = density === 'compact';
  // PO line cancellation / balance close (2026-09-14)
  const [lineCancel, setLineCancel] = useState<{ line: any; qty: number; reason: string; mrAction: POLineMrAction } | null>(null);
  const [lineDecision, setLineDecision] = useState<{ line: any; mrAction: POLineMrAction } | null>(null);
  const [lineCancelBusy, setLineCancelBusy] = useState(false);
  const openBalance = (line: any) => Math.max(0, Number(line.qty || 0) - Number(line.totalReceived || 0) - Number(line.cancelledQty || 0));

  const submitLineCancel = async () => {
    if (!po || !lineCancel) return;
    const max = openBalance(lineCancel.line);
    if (!(lineCancel.qty > 0) || lineCancel.qty > max) {
      message.warning(`Enter a quantity greater than 0 and up to ${max}`);
      return;
    }
    if (!lineCancel.reason.trim()) {
      message.warning('A reason is required');
      return;
    }
    setLineCancelBusy(true);
    try {
      await requestPOLineCancel(po.poId, lineCancel.line.id, {
        qty: lineCancel.qty,
        reason: lineCancel.reason.trim(),
        mrAction: lineCancel.mrAction,
      });
      message.success(`Line ${lineCancel.line.lineNo}: sent to the CEO for approval`);
      setLineCancel(null);
      fetchPO();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to request the cancellation');
    } finally {
      setLineCancelBusy(false);
    }
  };

  const decideLine = async (line: any, decision: 'approve' | 'reject', mrAction?: POLineMrAction) => {
    if (!po) return;
    setLineCancelBusy(true);
    try {
      await decidePOLineCancel(po.poId, line.id, { decision, mrAction });
      message.success(decision === 'approve'
        ? `Line ${line.lineNo}: cancellation approved`
        : `Line ${line.lineNo}: cancellation rejected, line kept`);
      setLineDecision(null);
      fetchPO();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to record the decision');
    } finally {
      setLineCancelBusy(false);
    }
  };

  const approveAllLineCancels = async () => {
    if (!po) return;
    const pending = (po.lines || []).filter((l: any) => l.cancelStatus === 'Pending');
    setLineCancelBusy(true);
    let done = 0;
    try {
      for (const l of pending) {
        await decidePOLineCancel(po.poId, l.id, { decision: 'approve' });
        done += 1;
      }
      message.success(`${done} line cancellation${done === 1 ? '' : 's'} approved`);
    } catch (err: any) {
      message.error(`${done} approved, then: ${err.response?.data?.error || 'failed'}`);
    } finally {
      setLineCancelBusy(false);
      fetchPO();
    }
  };
  const handleStatusChange = async (newStatus: string, reason?: string, mrAction?: POLineMrAction) => {
    if (!po) return;
    setStatusChanging(true);
    try {
      await updatePOStatus(po.id, newStatus, reason, mrAction);
      const label = newStatus === 'Pending Cancellation' ? 'Cancellation requested (pending CEO approval)'
        : newStatus === 'Cancelled' ? 'Cancellation approved'
        : newStatus === 'Ordered' && po.status === 'Pending Cancellation' ? 'Cancellation rejected - PO restored'
        : `PO status changed to ${newStatus}`;
      message.success(label);
      setCancelModalOpen(false);
      setCancelReason('');
      setCancelMrAction(null);
      setPoDecisionMrAction(null);
      fetchPO();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setStatusChanging(false);
    }
  };

  const fetchPO = useCallback(async () => {
    if (!poId) return;
    setLoading(true);
    try {
      const data = await getPO(poId as any);
      setPo(data);
    } catch (err) {
      message.error('Failed to load PO');
    } finally {
      setLoading(false);
    }
  }, [poId]);

  useEffect(() => {
    fetchPO();
  }, [fetchPO]);

  const canEdit = canAccess('purchasing.pos', 'full') && po && po.status !== 'Closed';
  const canReceive = canAccess('purchasing.pos', 'full') && po &&
    (po.status === 'Open' || po.status === 'Ordered' || po.status === 'Partially Received');
  const canRequestLineCancel = canAccess('purchasing.pos', 'full') && po && ['Open', 'Ordered', 'Partially Received'].includes(po.status);
  const canDecideLineCancel = (isApprover || isAdmin) && canAccess('purchasing.pos', 'full');
  const pendingLineCancels: any[] = (po?.lines || []).filter((l: any) => l.cancelStatus === 'Pending');
  const openLinesForCancel: any[] = (po?.lines || []).filter((l: any) => openBalance(l) > 0);
  // Item pictures (2026-09-22): thumbnail in the Item cell, so a PO reads as what is being bought.
  const lineItemIds = useMemo(() => (po?.lines || []).map((l: any) => l.mrLine?.catalogItem?.id ?? null), [po]);
  const { pictures: linePictures, reload: reloadLinePicture } = useItemPictures(lineItemIds);
  const poCancelPending = po?.status === 'Pending Cancellation';
  const pendingCancelValue = pendingLineCancels.reduce((s: number, l: any) => s + Number(l.cancelRequestQty || 0) * Number(l.actualUnitPrice ?? l.unitPrice ?? 0), 0);

  const startEdit = () => {
    const data: Record<number, any> = {};
    (po.lines || []).forEach((line: any) => {
      data[line.id] = {
        partNumber: line.partNumber || '',
        actualUnitPrice: line.actualUnitPrice ? Number(line.actualUnitPrice) : undefined,
      };
    });
    setEditData(data);
    setEditMode(true);
        const ids = (po?.lines || []).map((l: any) => l.mrLine?.catalogItem?.id).filter(Boolean);
        if (ids.length) catalogPricesBatch(ids).then(setPriceRef).catch(() => {});
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  const handleSaveEdits = async () => {
    const updates: Array<{ poLineId: number; partNumber?: string; actualUnitPrice?: number }> = [];

    for (const [lineId, data] of Object.entries(editData)) {
      const line = po.lines.find((l: any) => l.id === Number(lineId));
      if (!line) continue;

      const update: any = { poLineId: Number(lineId) };
      let hasChanges = false;

      if (data.partNumber !== undefined && data.partNumber !== (line.partNumber || '')) {
        update.partNumber = data.partNumber;
        hasChanges = true;
      }
      if (data.actualUnitPrice !== undefined && data.actualUnitPrice !== Number(line.actualUnitPrice || 0)) {
        update.actualUnitPrice = data.actualUnitPrice;
        hasChanges = true;
      }

      if (hasChanges) updates.push(update);
    }

    if (updates.length === 0) {
      message.info('No changes to save');
      setEditMode(false);
      return;
    }

    setSaving(true);
    try {
      await updatePOLines(po.poId, updates);
      message.success('PO lines updated successfully');
      setEditMode(false);
      fetchPO();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to update PO lines');
    } finally {
      setSaving(false);
    }
  };

  const openGrnModal = () => {
    const lines: Record<number, any> = {};
    (po.lines || []).forEach((line: any) => {
      const balance = Number(line.balanceQty || 0);
      if (balance > 0 && line.cancelStatus !== 'Pending') {
        lines[line.id] = {
          deliveredQty: 0,
          acceptedQty: 0,
          rejectedQty: 0,
          rejectReason: '',
          inspectionNotes: '',
        };
      }
    });
    setGrnForm({ deliveryNoteRef: '', supplierInvoice: '', notes: '', lines });
    setGrnModalOpen(true);
  };

  const handleGrnDeliveredChange = (lineId: number, delivered: number) => {
    setGrnForm((prev) => ({
      ...prev,
      lines: {
        ...prev.lines,
        [lineId]: {
          ...prev.lines[lineId],
          deliveredQty: delivered,
          acceptedQty: delivered, // default: accept all
          rejectedQty: 0,
        },
      },
    }));
  };

  const handleGrnAcceptedChange = (lineId: number, accepted: number) => {
    const delivered = grnForm.lines[lineId]?.deliveredQty || 0;
    setGrnForm((prev) => ({
      ...prev,
      lines: {
        ...prev.lines,
        [lineId]: {
          ...prev.lines[lineId],
          acceptedQty: accepted,
          rejectedQty: Math.max(0, delivered - accepted),
        },
      },
    }));
  };

  const handleSubmitGRN = async () => {
    if (!po) return;
    const grnLines = Object.entries(grnForm.lines)
      .filter(([, l]) => l.deliveredQty > 0)
      .map(([lineId, l]) => ({
        poLineId: Number(lineId),
        deliveredQty: l.deliveredQty,
        acceptedQty: l.acceptedQty,
        rejectedQty: l.rejectedQty,
        rejectReason: l.rejectedQty > 0 ? (l.rejectReason || 'other') : undefined,
        inspectionNotes: l.inspectionNotes || undefined,
      }));

    if (grnLines.length === 0) {
      message.warning('Enter delivered quantities for at least one line');
      return;
    }

    // Gate: check actual prices for lines being received
    const linesMissingPrice = grnLines
      .map((gl) => po.lines.find((l: any) => l.id === gl.poLineId))
      .filter((poLine: any) => poLine && poLine.actualUnitPrice == null);

    if (linesMissingPrice.length > 0) {
      const lineNos = linesMissingPrice.map((l: any) => l.lineNo).join(', ');
      message.error(`Actual price not entered for line(s): ${lineNos}. Use "Edit Part # / Actual Prices" to set them first.`);
      return;
    }

    // Validate
    for (const gl of grnLines) {
      const poLine = po.lines.find((l: any) => l.id === gl.poLineId);
      if (poLine && gl.acceptedQty > Number(poLine.balanceQty)) {
        message.error(`Accepted qty for line ${poLine.lineNo} exceeds balance`);
        return;
      }
      if (gl.rejectedQty > 0 && (!gl.rejectReason || gl.rejectReason === '')) {
        message.error(`Select a reject reason for line ${poLine?.lineNo}`);
        return;
      }
    }

    setReceiving(true);
    try {
      await createGRN({
        poId: po.poId,
        deliveryNoteRef: grnForm.deliveryNoteRef || undefined,
        supplierInvoice: grnForm.supplierInvoice || undefined,
        notes: grnForm.notes || undefined,
        lines: grnLines,
      });
      message.success('GRN created successfully');
      setGrnModalOpen(false);
      fetchPO();
    } catch (err: any) {
      message.error(err.response?.data?.error || 'Failed to create GRN');
    } finally {
      setReceiving(false);
    }
  };

  const fmtNum = (val: any) => val != null ? Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 }) : '-';

  // ─── Print PO as PDF ──────────────────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!po) return;

    const lines = po.lines || [];
    const estimTotal = Number(po.poTotal || 0);
    const actTotal = po.actualTotal ? Number(po.actualTotal) : null;

    // Derive unique departments from MR lines
    const deptMap = new Map<string, string>();
    lines.forEach((line: any) => {
      const dept = line.mrLine?.mrHeader?.department;
      if (dept) deptMap.set(dept.code, dept.name);
    });
    const departments = Array.from(deptMap.entries()); // [[code, name], ...]
    const deptDisplay = departments.length > 0
      ? departments.map(([code, name]) => name).join(' · ')
      : (po.department?.name || '—');

    const poDate = dayjs(po.poDate).format('DD MMMM YYYY');
    const currency = po.currency?.code || 'EGP';
    const esc = (v: any) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    // Cost allocation: per-line label + totals by cost center (what the accountants need on paper)
    const ccOf = (line: any) => (line.costCenter ? costCenterLabel(line.costCenter) : '');
    const ccTotals = new Map<string, number>();
    lines.forEach((line: any) => {
      const key = ccOf(line) || 'Unallocated';
      const t = Number(line.actualLineTotal != null ? line.actualLineTotal : line.lineTotal) || 0;
      ccTotals.set(key, (ccTotals.get(key) || 0) + t);
    });
    const ccRows = Array.from(ccTotals.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([k, v]) => `<tr><td style="color:${k === 'Unallocated' ? '#c00' : '#333'};padding:3px 0;font-size:12px;">${esc(k)}</td><td style="text-align:right;white-space:nowrap;padding:3px 0 3px 12px;font-size:12px;">${currency} ${fmtNum(v)}</td></tr>`)
      .join('');

    const tableRows = lines
      .map(
        (line: any, idx: number) => {
          const mrId = line.mrLine?.mrHeader?.mrId || '—';
          const unitPrice = line.actualUnitPrice != null ? line.actualUnitPrice : line.unitPrice;
          const lineTotal = line.actualLineTotal != null ? line.actualLineTotal : line.lineTotal;
          return `
      <tr>
        <td style="text-align:center;padding:10px 8px;border-bottom:1px solid #eee;">${line.lineNo || idx + 1}</td>
        <td style="padding:10px 8px;border-bottom:1px solid #eee;font-size:11px;color:#666;">${mrId}</td>
        <td style="padding:10px 8px;border-bottom:1px solid #eee;">${line.partNumber || '—'}</td>
        <td style="padding:10px 8px;border-bottom:1px solid #eee;">${line.itemDescription || ''}</td>
        <td style="padding:10px 8px;border-bottom:1px solid #eee;font-size:11px;color:${ccOf(line) ? '#333' : '#c00'};">${esc(ccOf(line) || 'Unallocated')}</td>
        <td style="text-align:center;padding:10px 8px;border-bottom:1px solid #eee;">${Number(line.cancelledQty || 0) > 0 ? `<s style="color:#999;">${Number(line.qty)}</s><br/><span style="font-size:10px;color:#c00;white-space:nowrap;">${Number(line.cancelledQty)} cancelled</span>` : Number(line.qty)}</td>
        <td style="text-align:right;padding:10px 8px;border-bottom:1px solid #eee;">${fmtNum(unitPrice)}</td>
        <td style="text-align:right;padding:10px 8px;border-bottom:1px solid #eee;">${fmtNum(lineTotal)}</td>
      </tr>`;
        }
      )
      .join('');

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>${po.poId} — Purchase Order</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=Inter:wght@300;400;500&display=swap');
    @page { margin: 20mm 12mm; }
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
      max-width: 780px;
      margin: 0 auto;
      color: #1a1a1a;
      padding: 30px 36px;
      font-size: 12.5px;
      line-height: 1.55;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    /* ── Header ───────────────────────────────── */
    .header {
      text-align: center;
      border-bottom: 2px solid #000;
      padding-bottom: 16px;
      margin-bottom: 22px;
    }
    .brand {
      font-family: 'Playfair Display', Georgia, serif;
      font-size: 26px;
      font-weight: 700;
      letter-spacing: 4px;
      text-transform: uppercase;
      margin-bottom: 3px;
    }
    .brand-sub {
      font-family: 'Inter', sans-serif;
      font-size: 10px;
      letter-spacing: 3px;
      text-transform: uppercase;
      color: #666;
    }
    .doc-title {
      font-family: 'Playfair Display', Georgia, serif;
      font-size: 16px;
      margin-top: 14px;
      letter-spacing: 2px;
    }
    .doc-ref {
      font-size: 11px;
      color: #666;
      margin-top: 4px;
    }

    /* ── Two-Column Layout ────────────────────── */
    .two-col {
      display: flex;
      justify-content: space-between;
      gap: 36px;
      margin-bottom: 20px;
    }
    .col { flex: 1; }
    .section-title {
      font-weight: 600;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      color: #000;
      border-bottom: 1px solid #ddd;
      padding-bottom: 4px;
      margin-bottom: 8px;
    }
    .detail-row { margin-bottom: 4px; }
    .detail-label {
      color: #888;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    .detail-value {
      font-size: 12.5px;
      color: #1a1a1a;
    }

    /* ── Department Tags ──────────────────────── */
    .dept-tags {
      display: flex;
      gap: 6px;
      flex-wrap: wrap;
      margin-top: 2px;
    }
    .dept-tag {
      display: inline-block;
      padding: 2px 10px;
      border-radius: 3px;
      font-size: 10.5px;
      font-weight: 500;
      letter-spacing: 0.5px;
      background: #f0f0f0;
      color: #333;
      border: 1px solid #ddd;
    }

    /* ── Items Table ──────────────────────────── */
    table {
      width: 100%;
      border-collapse: collapse;
      margin: 12px 0 10px;
    }
    th {
      background: #f8f8f8;
      padding: 7px 8px;
      text-align: left;
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 1px;
      font-weight: 600;
      border-bottom: 2px solid #000;
      color: #333;
    }
    tbody td {
      font-size: 12px;
      color: #1a1a1a;
    }

    /* ── Totals ───────────────────────────────── */
    .totals-section {
      display: flex;
      justify-content: flex-end;
      margin-top: 6px;
    }
    .totals-table {
      width: 280px;
    }
    .totals-table tr td {
      padding: 5px 0;
      font-size: 12.5px;
    }
    .totals-table .grand-total {
      border-top: 2px solid #000;
      font-size: 15px;
      font-weight: 700;
    }

    /* ── Notes ────────────────────────────────── */
    .notes {
      margin-top: 22px;
      padding-top: 14px;
      border-top: 1px solid #ddd;
    }
    .notes h4 {
      font-size: 10px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      margin-bottom: 8px;
    }
    .notes-content {
      font-size: 11.5px;
      color: #555;
      line-height: 1.7;
    }

    /* ── Footer ───────────────────────────────── */
    .footer {
      margin-top: 28px;
      text-align: center;
      padding-top: 14px;
      border-top: 2px solid #000;
      font-size: 10px;
      color: #888;
    }
    .footer .brand-sm {
      font-family: 'Playfair Display', Georgia, serif;
      font-size: 13px;
      font-weight: 600;
      letter-spacing: 2px;
      color: #000;
      margin-bottom: 3px;
    }

    @media print {
      body { padding: 0; margin: 0; }
      .header { page-break-after: avoid; }
      table { page-break-inside: auto; }
      tr { page-break-inside: avoid; page-break-after: auto; }
      .notes { page-break-inside: avoid; }
      .footer { page-break-inside: avoid; }
    }
  </style>
</head>
<body>

  <!-- Header -->
  <div class="header">
    <div class="brand">Shahira Lasheen</div>
    <div class="brand-sub">Procurement Department</div>
    <div class="doc-title">Purchase Order</div>
    <div class="doc-ref">${po.poId} &nbsp;&bull;&nbsp; ${poDate}</div>
  </div>

  <!-- Two-Column Info -->
  <div class="two-col">
    <div class="col">
      <div class="section-title">Vendor</div>
      <div class="detail-row">
        <div class="detail-value" style="font-size:16px;font-weight:600;">${po.vendor?.name || '—'}</div>
      </div>
      ${po.vendor?.contactPerson ? `<div class="detail-row"><div class="detail-label">Contact</div><div class="detail-value">${po.vendor.contactPerson}</div></div>` : ''}
      ${po.vendor?.phone ? `<div class="detail-row"><div class="detail-label">Phone</div><div class="detail-value">${po.vendor.phone}</div></div>` : ''}
      ${po.vendor?.email ? `<div class="detail-row"><div class="detail-label">Email</div><div class="detail-value">${po.vendor.email}</div></div>` : ''}
      ${po.vendor?.address ? `<div class="detail-row"><div class="detail-label">Address</div><div class="detail-value">${po.vendor.address}</div></div>` : ''}
      <div class="section-title" style="margin-top:18px;">Cost Allocation</div>
      <table style="width:100%;border-collapse:collapse;">${ccRows}</table>
    </div>
    <div class="col">
      <div class="section-title">Order Details</div>
      <div class="detail-row"><div class="detail-label">PO Number</div><div class="detail-value">${po.poId}</div></div>
      <div class="detail-row"><div class="detail-label">Date</div><div class="detail-value">${poDate}</div></div>
      <div class="detail-row"><div class="detail-label">Status</div><div class="detail-value">${po.status}</div></div>
      <div class="detail-row"><div class="detail-label">Currency</div><div class="detail-value">${currency}</div></div>
      <div class="detail-row"><div class="detail-label">Created By</div><div class="detail-value">${po.createdBy?.displayName || '—'}</div></div>
      <div class="detail-row">
        <div class="detail-label">Department${departments.length > 1 ? 's' : ''}</div>
        <div class="detail-value">${departments.length > 1
          ? `<div class="dept-tags">${departments.map(([code, name]) => `<span class="dept-tag">${name}</span>`).join('')}</div>`
          : deptDisplay
        }</div>
      </div>
    </div>
  </div>

  <!-- Items Table -->
  <div class="section-title" style="margin-bottom:0;">Line Items</div>
  <table>
    <thead>
      <tr>
        <th style="text-align:center;width:30px;">#</th>
        <th style="width:90px;">MR Ref</th>
        <th style="width:90px;">Part No.</th>
        <th>Description</th>
        <th style="width:130px;">Cost Center</th>
        <th style="text-align:center;width:45px;">Qty</th>
        <th style="text-align:right;width:85px;">Unit Price</th>
        <th style="text-align:right;width:90px;">Total</th>
      </tr>
    </thead>
    <tbody>
      ${tableRows}
    </tbody>
  </table>

  <!-- Totals -->
  <div class="totals-section">
    <table class="totals-table">
      <tr>
        <td style="color:#666;">Estimated Total</td>
        <td style="text-align:right;">${currency} ${fmtNum(estimTotal)}</td>
      </tr>
      ${Number(po.cancelledValue || 0) > 0 ? `<tr><td style="color:#c00;">Cancelled</td><td style="text-align:right;color:#c00;">- ${currency} ${fmtNum(po.cancelledValue)}</td></tr>` : ''}
      ${actTotal !== null ? `
      <tr class="grand-total">
        <td>Actual Total</td>
        <td style="text-align:right;">${currency} ${fmtNum(actTotal)}</td>
      </tr>
      ` : `
      <tr class="grand-total">
        <td>Total</td>
        <td style="text-align:right;">${currency} ${fmtNum(estimTotal)}</td>
      </tr>
      `}
    </table>
  </div>


  ${po.notes ? `
  <!-- Notes -->
  <div class="notes">
    <h4>Notes</h4>
    <div class="notes-content">${po.notes.replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br/>')}</div>
  </div>
  ` : ''}

  <!-- Footer -->
  <div class="footer">
    <div class="brand-sm">Shahira Lasheen</div>
    <div>Procurement Department &nbsp;&bull;&nbsp; Cairo, Egypt</div>
    <div style="margin-top:4px;">erp.shahiralasheen.com &nbsp;&bull;&nbsp; Generated ${dayjs().format('DD MMM YYYY [at] HH:mm')}</div>
  </div>

</body>
</html>`;

    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(html);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 400);
    }
  };

  const lineColumns: any[] = [
    { title: '#', dataIndex: 'lineNo', width: 50, align: 'center' as const },
    {
      title: 'Source MR',
      key: 'sourceMR',
      width: 140,
      render: (_: any, record: any) => {
        const mrHeader = record.mrLine?.mrHeader;
        if (!mrHeader) return <span style={{ color: '#999' }}>—</span>;
        return (
          <div>
            <a onClick={() => navigate(`/mrs/${mrHeader.id}`)} style={{ cursor: 'pointer' }}>{mrHeader.mrId}</a>
            <br />
            <Tag style={{ fontSize: 11, marginTop: 2 }}>{mrHeader.department?.code}</Tag>
          </div>
        );
      },
    },
    {
      // Flexible on purpose: the code must never be clipped or wrapped mid-code.
      title: 'Item',
      dataIndex: 'partNumber',
      // ConfigurableTable is tableLayout="fixed": without a numeric width this column collapses.
      // 440 + 46 for the 36px picture thumbnail that now sits before the code (2026-09-22).
      width: 486,
      render: (val: string, record: any) => {
        const code = record.mrLine?.catalogItem?.partNumber || val;
        const description = record.mrLine?.catalogItem?.description || record.itemDescription;
        const descriptionAr = record.mrLine?.catalogItem?.descriptionAr;
        const ciId = record.mrLine?.catalogItem?.id ?? null;
        if (editMode) {
          return (
            <div>
              <Input
                size="small"
                value={editData[record.id]?.partNumber ?? val ?? ''}
                onChange={(e) => setEditData((prev) => ({
                  ...prev,
                  [record.id]: { ...prev[record.id], partNumber: e.target.value },
                }))}
                placeholder="Enter part #"
              />
              <div style={{ marginTop: 4 }}>
                <ItemCell description={description} descriptionAr={descriptionAr} compact={compact} />
              </div>
            </div>
          );
        }
        const cell = (
          <ItemCell
            partNumber={code}
            description={description}
            descriptionAr={descriptionAr}
            linkTo={record.mrLine?.catalogItem?.id ? `/catalog/${record.mrLine.catalogItem.id}` : undefined}
            compact={compact}
            showThumb
            pictureUrl={ciId ? linePictures[ciId]?.url : null}
            thumbItemId={ciId}
            onThumbError={ciId ? () => reloadLinePicture(ciId) : undefined}
          />
        );
        if (code) return cell;
        return (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            {freeTextMark}
            <div style={{ flex: 1, minWidth: 0 }}>{cell}</div>
          </div>
        );
      },
    },
    {
      title: 'Cost Center',
      key: 'costCenter',
      width: 180,
      ellipsis: true,
      render: (_: any, record: any) => record.costCenter ? costCenterLabel(record.costCenter) : <Text type="secondary">—</Text>,
    },
    {
      title: 'Qty',
      dataIndex: 'qty',
      width: 100,
      align: 'right' as const,
      render: (val: any, record: any) => <span style={{ whiteSpace: 'nowrap' }}>{Number(val)} {record.unit?.name || ''}</span>,
    },
    {
      title: 'Est. Price',
      dataIndex: 'unitPrice',
      width: 120,
      align: 'right' as const,
      render: (val: any) => (
        <Tooltip title="Estimated by requester">
          <span>
            <MoneyCell
              amount={val == null ? null : Number(val)}
              rule="requester est."
              align="right"
              compact={compact}
            />
          </span>
        </Tooltip>
      ),
    },
    {
      title: 'Actual Price',
      dataIndex: 'actualUnitPrice',
      width: 130,
      align: 'right' as const,
      render: (val: any, record: any) => {
        if (editMode) {
          return (
            <div>
            <InputNumber
              size="small"
              min={0}
              step={0.01}
              value={editData[record.id]?.actualUnitPrice ?? (val ? Number(val) : undefined)}
              onChange={(v) => setEditData((prev) => ({
                ...prev,
                [record.id]: { ...prev[record.id], actualUnitPrice: v || undefined },
              }))}
              placeholder="Actual"
              style={{ width: '100%' }}
            />
              {(() => {
                const pr = priceRef[record.mrLine?.catalogItem?.id]; if (!pr) return null;
                const vp = pr.vendors.find((x) => x.vendorId === po.vendor?.id) || pr.vendors.find((x) => x.preferred);
                const cur = editData[record.id]?.actualUnitPrice ?? (val ? Number(val) : undefined);
                const pct = (base: number | null | undefined) => base && cur != null ? Math.round(((cur / base) - 1) * 1000) / 10 : null;
                const c = (n: number | null) => n == null ? undefined : n > 0 ? '#c0392b' : n < 0 ? '#1f8a4c' : undefined;
                return (
                  <div style={{ fontSize: 11, color: '#666', lineHeight: 1.5 }}>
                    {vp?.listPrice != null && <div>list {Number(vp.listPrice).toLocaleString('en-US', { minimumFractionDigits: 2 })}{pct(vp.listPrice) != null && <span style={{ color: c(pct(vp.listPrice)) }}> ({pct(vp.listPrice)! > 0 ? '+' : ''}{pct(vp.listPrice)}%)</span>}</div>}
                    {vp?.lastPaid != null && <div>last paid {Number(vp.lastPaid).toLocaleString('en-US', { minimumFractionDigits: 2 })} {vp.lastPo}{pct(vp.lastPaid) != null && <span style={{ color: c(pct(vp.lastPaid)) }}> ({pct(vp.lastPaid)! > 0 ? '+' : ''}{pct(vp.lastPaid)}%)</span>}</div>}
                  </div>
                );
              })()}
            </div>
          );
        }
        return (
          <MoneyCell
            amount={val == null ? null : Number(val)}
            rule={val == null ? null : 'actual'}
            align="right"
            compact={compact}
            missingLabel="not set"
          />
        );
      },
    },
    {
      title: 'Est. Total',
      dataIndex: 'lineTotal',
      width: 110,
      align: 'right' as const,
      render: (val: any) => (
        <MoneyCell amount={val == null ? null : Number(val)} align="right" compact={compact} />
      ),
    },
    {
      title: 'Actual Total',
      dataIndex: 'actualLineTotal',
      width: 110,
      align: 'right' as const,
      render: (val: any) => (
        <MoneyCell
          amount={val == null ? null : Number(val)}
          align="right"
          compact={compact}
          missingLabel="no actual yet"
        />
      ),
    },
    {
      title: 'Variance',
      dataIndex: 'priceVariance',
      width: 90,
      align: 'right' as const,
      render: (val: any) => {
        if (val == null) return '-';
        const v = Number(val);
        const color = v > 5 ? 'red' : v < -5 ? 'green' : 'default';
        return <Tag color={color}>{v > 0 ? '+' : ''}{v.toFixed(1)}%</Tag>;
      },
    },
    {
      title: 'Received',
      dataIndex: 'totalReceived',
      width: 150,
      render: (val: any, record: any) =>
        Number(record.cancelledQty || 0) > 0 || record.cancelStatus === 'Pending' ? (
          <QtySplitCell
            ordered={Number(record.qty || 0)}
            received={Number(val || 0)}
            cancelled={Number(record.cancelledQty || 0)}
            pending={record.cancelStatus === 'Pending' ? Number(record.cancelRequestQty || 0) : 0}
            suffix={record.unit?.name || undefined}
            compact={compact}
          />
        ) : (
          <ProgressCell
            done={Number(val || 0)}
            total={Number(record.qty || 0)}
            suffix={record.unit?.name || undefined}
            compact={compact}
          />
        ),
    },
    {
      title: 'Balance',
      dataIndex: 'balanceQty',
      width: 80,
      align: 'right' as const,
      render: (val: any) => <Text type={Number(val) > 0 ? 'warning' : 'success'}>{Number(val || 0)}</Text>,
    },
    {
      title: 'GRN',
      key: 'grn',
      width: 120,
      render: (_: any, record: any) => {
        const grnLinks = record.grnLines || [];
        if (grnLinks.length === 0) return <Text type="secondary">—</Text>;
        const uniqueGrns = new Map<number, string>();
        grnLinks.forEach((gl: any) => {
          if (gl.grnHeader) uniqueGrns.set(gl.grnHeader.id, gl.grnHeader.grnId);
        });
        return (
          <Space direction="vertical" size={0}>
            {Array.from(uniqueGrns.entries()).map(([id, grnId]) => (
              <a key={id} onClick={() => navigate(`/grns/${grnId}`)} style={{ cursor: 'pointer', fontSize: 12 }}>{grnId}</a>
            ))}
          </Space>
        );
      },
    },
  ];

  // PO line cancellation / balance close (2026-09-14): request, pending details + CEO decision, outcome.
  if (!editMode) {
    lineColumns.push({
      title: 'Line action',
      key: 'lineCancel',
      width: 280,
      render: (_: any, record: any) => {
        const received = Number(record.totalReceived || 0);
        const cancelled = Number(record.cancelledQty || 0);
        const unit = record.unit?.name || '';
        if (record.cancelStatus === 'Pending') {
          return (
            <div style={{ fontSize: 12, lineHeight: 1.45, whiteSpace: 'normal' }}>
              <StatusPill tone="warn" label={received > 0 ? 'Balance close pending' : 'Cancellation pending'} />
              <div style={{ marginTop: 4 }}>
                <strong>{Number(record.cancelRequestQty || 0)} {unit}</strong> · {record.cancelReason}
              </div>
              <div style={{ color: '#8a94a6' }}>
                {record.cancelRequestedBy?.displayName ? `${record.cancelRequestedBy.displayName} · ` : ''}
                {record.cancelMrAction === 'reorder' ? 'order it again' : 'no longer needed'}
              </div>
              {poCancelPending && <div style={{ color: '#8a94a6' }}>part of the PO cancellation request</div>}
              {canDecideLineCancel && !poCancelPending && (
                <Space size={6} style={{ marginTop: 6 }}>
                  <Button size="small" loading={lineCancelBusy} onClick={() => decideLine(record, 'reject')}>
                    Reject, keep line
                  </Button>
                  <Button size="small" type="primary" danger onClick={() => setLineDecision({ line: record, mrAction: record.cancelMrAction || 'reorder' })}>
                    Approve
                  </Button>
                </Space>
              )}
            </div>
          );
        }
        const outcome = cancelled > 0 ? (
          <div style={{ fontSize: 12, lineHeight: 1.45, whiteSpace: 'normal' }}>
            <StatusPill tone={received > 0 ? 'mute' : 'bad'} label={received > 0 ? 'Balance closed' : 'Cancelled'} />
            <div style={{ color: '#8a94a6' }}>
              {cancelled} {unit} cancelled
              {record.cancelDecidedBy?.displayName ? ` · ${record.cancelDecidedBy.displayName}` : ''}
              {record.cancelDecidedAt ? ` · ${dayjs(record.cancelDecidedAt).format('DD MMM')}` : ''}
            </div>
          </div>
        ) : record.cancelStatus === 'Rejected' ? (
          <div style={{ fontSize: 12, color: '#8a94a6' }}>last cancellation request rejected</div>
        ) : null;
        if (canRequestLineCancel && openBalance(record) > 0) {
          return (
            <div>
              {outcome}
              <Button
                size="small"
                danger
                style={{ marginTop: outcome ? 4 : 0 }}
                onClick={() => setLineCancel({ line: record, qty: openBalance(record), reason: '', mrAction: 'reorder' })}
              >
                {received > 0 ? 'Close balance' : 'Cancel line'}
              </Button>
            </div>
          );
        }
        return outcome;
      },
    });
  }

  const receiptColumns = [
    { title: 'Date', dataIndex: 'receivedOn', width: 160, render: (val: string) => dayjs(val).format('YYYY-MM-DD HH:mm') },
    { title: 'Line #', dataIndex: 'poLineId', width: 70, align: 'center' as const },
    { title: 'Qty Received', dataIndex: 'receivedQty', width: 110, align: 'right' as const, render: (v: any) => Number(v) },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true, render: (val: string) => val || '-' },
  ];

  // Lines visible in GRN modal that are missing actual prices
  const grnModalMissingPrices = useMemo(() => {
    if (!po) return [];
    return (po.lines || [])
      .filter((l: any) => Number(l.balanceQty || 0) > 0)
      .filter((l: any) => l.actualUnitPrice == null);
  }, [po]);

  // Whether any line the user is actively receiving lacks actual price
  const grnHasMissingPrices = useMemo(() => {
    if (!po || !grnForm.lines) return false;
    return Object.entries(grnForm.lines)
      .filter(([, l]) => l.deliveredQty > 0)
      .some(([lineId]) => {
        const poLine = (po.lines || []).find((l: any) => l.id === Number(lineId));
        return poLine && poLine.actualUnitPrice == null;
      });
  }, [po, grnForm.lines]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>;
  if (!po) return <div>PO not found</div>;

  const hasActualPrices = po.lines?.some((l: any) => l.actualUnitPrice != null);

  const estimatedTotal = Number(po.poTotal || 0);
  const actualTotal = po.actualTotal ? Number(po.actualTotal) : null;
  const totalVariance = actualTotal !== null && estimatedTotal > 0
    ? ((actualTotal - estimatedTotal) / estimatedTotal * 100).toFixed(1)
    : null;

  const departments = (() => {
    const depts = new Map<number, { code: string; name: string }>();
    po?.lines?.forEach((line: any) => {
      const dept = line.mrLine?.mrHeader?.department;
      if (dept && !depts.has(dept.id)) {
        depts.set(dept.id, { code: dept.code, name: dept.name });
      }
    });
    return depts.size > 0
      ? Array.from(depts.values())
      : po?.department
        ? [{ code: po.department.code || '', name: po.department.name }]
        : [];
  })();

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate('/pos'))}>Back</Button>
            <Title level={4} style={{ margin: 0 }}>{po.poId}</Title>
            <Tag color={STATUS_TAG_COLORS[po.status] || 'default'} style={{ fontSize: 14, padding: '2px 12px' }}>
              {po.status}
            </Tag>
          </Space>
        </Col>
        <Col>
          <Space>
            {canAccess('purchasing.pos', 'full') && po.status === 'Open' && !editMode && (
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={statusChanging}
                onClick={() => handleStatusChange('Ordered')}
              >
                Mark as Ordered
              </Button>
            )}
            {/* Procurement officer requests cancellation (goes to Pending Cancellation for CEO approval) */}
            {canAccess('purchasing.pos', 'full') && ['Open', 'Ordered', 'Partially Received'].includes(po.status) && !editMode && (
              <Button
                danger
                icon={<StopOutlined />}
                loading={statusChanging}
                disabled={pendingLineCancels.length > 0}
                title={pendingLineCancels.length > 0 ? 'Decide the pending line cancellations first' : undefined}
                onClick={() => { setCancelReason(''); setCancelMrAction(null); setCancelModalOpen(true); }}
              >
                Request Cancellation
              </Button>
            )}
            {/* CEO sees Pending Cancellation and can approve or reject */}
            {(isApprover || isAdmin) && po.status === 'Pending Cancellation' && !editMode && (
              <>
                <Button
                  danger
                  icon={<StopOutlined />}
                  loading={statusChanging}
                  onClick={() => handleStatusChange('Cancelled', undefined, poDecisionMrAction || pendingLineCancels[0]?.cancelMrAction)}
                >
                  Approve Cancellation
                </Button>
                <Button
                  type="primary"
                  icon={<CheckCircleOutlined />}
                  loading={statusChanging}
                  onClick={() => handleStatusChange('Ordered')}
                >
                  Reject Cancellation (Restore)
                </Button>
              </>
            )}
            {isAdmin && ['Received', 'Partially Received', 'Ordered'].includes(po.status) && !editMode && (
              <Button
                loading={statusChanging}
                onClick={() => handleStatusChange('Closed')}
              >
                Close PO
              </Button>
            )}
            {!editMode && (
              <Button icon={<PrinterOutlined />} onClick={handlePrintPDF}>
                Print PDF
              </Button>
            )}
            {canEdit && !editMode && (
              <Button icon={<EditOutlined />} onClick={startEdit}>
                Edit Part # / Actual Prices
              </Button>
            )}
            {editMode && (
              <>
                <Button icon={<CloseOutlined />} onClick={cancelEdit}>Cancel</Button>
                <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={handleSaveEdits}>
                  Save Changes
                </Button>
              </>
            )}
          </Space>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
          <Descriptions.Item label="PO ID">{po.poId}</Descriptions.Item>
          <Descriptions.Item label="Department">
            {departments.map((dept, i) => {
              const colors = ['blue', 'green', 'orange', 'purple', 'cyan', 'magenta'];
              return <Tag key={dept.code} color={colors[i % colors.length]}>{dept.code} - {dept.name}</Tag>;
            })}
          </Descriptions.Item>
          <Descriptions.Item label="Vendor">{po.vendor?.name || '-'}</Descriptions.Item>
          <Descriptions.Item label="PO Date">{dayjs(po.poDate).format('YYYY-MM-DD')}</Descriptions.Item>
          <Descriptions.Item label="Created By">{po.createdBy?.displayName || '-'}</Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={STATUS_TAG_COLORS[po.status] || 'default'}>{po.status}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Estimated Total">
            <Text type="secondary">{fmtNum(estimatedTotal)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Actual Total">
            {actualTotal !== null ? (
              <Space>
                <Text strong>{fmtNum(actualTotal)}</Text>
                {totalVariance !== null && (
                  <Tag color={Number(totalVariance) > 5 ? 'red' : Number(totalVariance) < -5 ? 'green' : 'blue'}>
                    {Number(totalVariance) > 0 ? '+' : ''}{totalVariance}%
                  </Tag>
                )}
              </Space>
            ) : (
              <Text type="secondary" italic>Pending officer input</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Received Value">
            <Text type="success">{fmtNum(po.receivedValue)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Open Value">{fmtNum(po.openValue)}</Descriptions.Item>
          {Number(po.cancelledValue || 0) > 0 && (
            <Descriptions.Item label="Cancelled Value">
              <Text type="danger">{fmtNum(po.cancelledValue)}</Text>
            </Descriptions.Item>
          )}
          {po.notes && (
            <Descriptions.Item label="Notes" span={3}>{po.notes}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {po.status === 'Pending Cancellation' && (() => {
        const proposed: POLineMrAction = (pendingLineCancels[0]?.cancelMrAction as POLineMrAction) || 'not_needed';
        const chosen = poDecisionMrAction || proposed;
        const anyReceived = pendingLineCancels.some((l: any) => Number(l.totalReceived || 0) > 0);
        const allReceivedNothing = (po.lines || []).every((l: any) => Number(l.totalReceived || 0) === 0);
        const after = allReceivedNothing ? 'Cancelled' : 'Received';
        return (
          <Alert
            type="warning"
            showIcon
            message="Cancellation Pending CEO Approval"
            description={
              <div>
                <div>
                  <strong>Reason:</strong> {po.cancelReason || 'No reason provided'}
                  {po.cancelRequestedBy && (
                    <> &mdash; <strong>Requested by:</strong> {po.cancelRequestedBy.displayName}</>
                  )}
                </div>
                {pendingLineCancels.length > 0 && (
                  <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                    {pendingLineCancels.map((l: any) => {
                      const received = Number(l.totalReceived || 0);
                      const price = Number(l.actualUnitPrice ?? l.unitPrice ?? 0);
                      const q = Number(l.cancelRequestQty || 0);
                      return (
                        <li key={l.id}>
                          Line {l.lineNo} · {l.mrLine?.catalogItem?.partNumber || l.partNumber || l.itemDescription} ·{' '}
                          {received > 0 ? `close balance ${q} of ${Number(l.qty)}` : `cancel ${q} of ${Number(l.qty)}`} {l.unit?.name || ''} · {fmtNum(Math.round(q * price * 100) / 100)} EGP
                        </li>
                      );
                    })}
                    {(po.lines || []).filter((l: any) => l.cancelStatus !== 'Pending' && openBalance(l) === 0).map((l: any) => (
                      <li key={`kept-${l.id}`} style={{ color: '#8a94a6' }}>Line {l.lineNo} · fully received or already closed, not affected</li>
                    ))}
                  </ul>
                )}
                {pendingLineCancels.length > 0 && (
                  <div style={{ marginTop: 8 }}>
                    <strong>MR outcome:</strong>{' '}
                    {isApprover || isAdmin ? (
                      <Radio.Group
                        size="small"
                        value={chosen}
                        onChange={(e) => setPoDecisionMrAction(e.target.value)}
                        options={[
                          { label: 'No longer needed', value: 'not_needed' },
                          { label: 'Order it again', value: 'reorder' },
                        ]}
                        optionType="button"
                      />
                    ) : (
                      chosen === 'reorder' ? 'order it again' : 'no longer needed'
                    )}
                    {chosen !== proposed && <span style={{ marginLeft: 8, color: '#8a94a6', fontSize: 12 }}>(procurement proposed {proposed === 'reorder' ? 'order it again' : 'no longer needed'})</span>}
                  </div>
                )}
                {pendingLineCancels.length > 0 && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                    Approving cancels {fmtNum(Math.round(pendingCancelValue * 100) / 100)} EGP of open value. {po.poId} becomes {after}.
                    {anyReceived ? ' Received quantities stay; to send them back, use a GRN return.' : ''} Receiving is locked until decided.
                  </div>
                )}
              </div>
            }
            style={{ marginBottom: 16 }}
          />
        );
      })()}

      {pendingLineCancels.length > 0 && !poCancelPending && (
        <Alert
          type="warning"
          showIcon
          message={`${pendingLineCancels.length} line cancellation${pendingLineCancels.length === 1 ? '' : 's'} waiting for CEO approval`}
          description={canDecideLineCancel
            ? 'Approve or reject each one in the Line action column.'
            : 'Receiving on those lines is locked until the CEO decides.'}
          action={canDecideLineCancel && pendingLineCancels.length > 1 ? (
            <Button size="small" type="primary" danger loading={lineCancelBusy} onClick={approveAllLineCancels}>
              Approve all
            </Button>
          ) : undefined}
          style={{ marginBottom: 16 }}
        />
      )}

      {!hasActualPrices && canEdit && !editMode && (
        <Alert
          type="info"
          showIcon
          message="Actual prices not yet entered"
          description="Click 'Edit Part # / Actual Prices' to enter the actual unit prices and correct part numbers after purchasing."
          style={{ marginBottom: 16 }}
        />
      )}

      <Card
        title="PO Lines"
        style={{ marginBottom: 16 }}
        extra={
          <Space>
            <DensityToggle value={density} onChange={setDensity} />
            {canReceive && !editMode && (
              <>
                <Button type="primary" icon={<InboxOutlined />} onClick={openGrnModal}>
                  Receive Goods (GRN)
                </Button>
              </>
            )}
          </Space>
        }
      >
        <ConfigurableTable
          tableId="po-view-lines"
          columns={lineColumns}
          dataSource={po.lines}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 2246 }}
        />
      </Card>

      {po.lines?.some((l: any) => l.receipts && l.receipts.length > 0) && (
        <Card title="Receipt History">
          <ConfigurableTable
            tableId="po-view-receipts"
            columns={receiptColumns}
            dataSource={po.lines.flatMap((l: any) =>
              (l.receipts || []).map((r: any) => ({ ...r, lineNo: l.lineNo }))
            )}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            size="small"
          />
        </Card>
      )}

      {po.grns && po.grns.length > 0 && (
        <Card title="Goods Receiving Notes (GRNs)" style={{ marginTop: 16 }}>
          <ConfigurableTable
            tableId="po-view-grns"
            columns={[
              {
                title: 'GRN ID',
                dataIndex: 'grnId',
                width: 160,
                render: (val: string) => (
                  <a onClick={() => navigate(`/grns/${val}`)} style={{ cursor: 'pointer', fontWeight: 500 }}>{val}</a>
                ),
              },
              {
                title: 'Received On',
                dataIndex: 'receivedOn',
                width: 180,
                render: (val: string) => dayjs(val).format('DD MMM YYYY — HH:mm'),
              },
              {
                title: 'Lines',
                dataIndex: 'lineCount',
                width: 80,
                align: 'center' as const,
              },
            ]}
            dataSource={po.grns}
            rowKey="id"
            pagination={false}
            size="small"
          />
        </Card>
      )}

      {po && (
        <Card style={{ marginTop: 16 }}>
          <EntityHistory entityType="PO" entityId={po.poId} />
        </Card>
      )}

      {/* GRN Modal */}
      <Modal
        title="Create Goods Receiving Note (GRN)"
        open={grnModalOpen}
        onOk={handleSubmitGRN}
        onCancel={() => setGrnModalOpen(false)}
        confirmLoading={receiving}
        okText="Create GRN"
        okButtonProps={{ disabled: grnHasMissingPrices }}
        width={1000}
      >
        {grnModalMissingPrices.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${grnModalMissingPrices.length} line(s) missing actual prices`}
            description={
              <>
                Line(s) {grnModalMissingPrices.map((l: any) => l.lineNo).join(', ')} do not have actual prices set.
                Close this dialog and click <strong>"Edit Part # / Actual Prices"</strong> to enter them before receiving.
              </>
            }
            style={{ marginBottom: 16 }}
          />
        )}
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Delivery Note Ref</Text>
            </div>
            <Input
              value={grnForm.deliveryNoteRef}
              onChange={(e) => setGrnForm((f) => ({ ...f, deliveryNoteRef: e.target.value }))}
              placeholder="e.g., DN-2026-0451"
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Supplier Invoice #</Text>
            </div>
            <Input
              value={grnForm.supplierInvoice}
              onChange={(e) => setGrnForm((f) => ({ ...f, supplierInvoice: e.target.value }))}
              placeholder="e.g., INV-9821"
            />
          </Col>
          <Col span={8}>
            <div style={{ marginBottom: 4 }}>
              <Text strong>Notes</Text>
            </div>
            <Input
              value={grnForm.notes}
              onChange={(e) => setGrnForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="General notes..."
            />
          </Col>
        </Row>

        <Table
          dataSource={(po?.lines || []).filter((l: any) => Number(l.balanceQty || 0) > 0 && l.cancelStatus !== 'Pending')}
          rowKey="id"
          pagination={false}
          size="small"
          scroll={{ x: 850 }}
          columns={[
            { title: '#', dataIndex: 'lineNo', width: 40, align: 'center' as const },
            { title: 'Description', dataIndex: 'itemDescription', width: 160, ellipsis: true },
            {
              title: 'Actual Price',
              width: 100,
              align: 'right' as const,
              render: (_: any, record: any) => {
                if (record.actualUnitPrice == null) {
                  return (
                    <Tooltip title="Actual price must be set before receiving">
                      <Tag color="red" icon={<WarningOutlined />}>Missing</Tag>
                    </Tooltip>
                  );
                }
                return <Text>{fmtNum(record.actualUnitPrice)}</Text>;
              },
            },
            {
              title: 'Balance',
              dataIndex: 'balanceQty',
              width: 70,
              align: 'right' as const,
              render: (val: any) => <Text strong>{Number(val)}</Text>,
            },
            {
              title: 'Delivered',
              width: 90,
              render: (_: any, record: any) => (
                <InputNumber
                  size="small"
                  min={0}
                  max={Number(record.balanceQty)}
                  value={grnForm.lines[record.id]?.deliveredQty || 0}
                  onChange={(v) => handleGrnDeliveredChange(record.id, v || 0)}
                  style={{ width: '100%' }}
                />
              ),
            },
            {
              title: 'Accepted',
              width: 90,
              render: (_: any, record: any) => {
                const delivered = grnForm.lines[record.id]?.deliveredQty || 0;
                return (
                  <InputNumber
                    size="small"
                    min={0}
                    max={delivered}
                    value={grnForm.lines[record.id]?.acceptedQty || 0}
                    onChange={(v) => handleGrnAcceptedChange(record.id, v || 0)}
                    style={{ width: '100%' }}
                    disabled={delivered === 0}
                  />
                );
              },
            },
            {
              title: 'Rejected',
              width: 70,
              align: 'right' as const,
              render: (_: any, record: any) => {
                const rejected = grnForm.lines[record.id]?.rejectedQty || 0;
                return rejected > 0 ? <Text type="danger">{rejected}</Text> : <Text type="secondary">0</Text>;
              },
            },
            {
              title: 'Reject Reason',
              width: 140,
              render: (_: any, record: any) => {
                const rejected = grnForm.lines[record.id]?.rejectedQty || 0;
                if (rejected === 0) return <Text type="secondary">-</Text>;
                return (
                  <Select
                    size="small"
                    value={grnForm.lines[record.id]?.rejectReason || undefined}
                    onChange={(v) => setGrnForm((prev) => ({
                      ...prev,
                      lines: { ...prev.lines, [record.id]: { ...prev.lines[record.id], rejectReason: v } },
                    }))}
                    placeholder="Select..."
                    style={{ width: '100%' }}
                  >
                    <Select.Option value="damaged">Damaged</Select.Option>
                    <Select.Option value="wrong_item">Wrong Item</Select.Option>
                    <Select.Option value="wrong_spec">Wrong Spec</Select.Option>
                    <Select.Option value="defective">Defective</Select.Option>
                    <Select.Option value="short_delivery">Short Delivery</Select.Option>
                    <Select.Option value="other">Other</Select.Option>
                  </Select>
                );
              },
            },
            {
              title: 'Inspection Notes',
              width: 160,
              render: (_: any, record: any) => (
                <Input
                  size="small"
                  value={grnForm.lines[record.id]?.inspectionNotes || ''}
                  onChange={(e) => setGrnForm((prev) => ({
                    ...prev,
                    lines: { ...prev.lines, [record.id]: { ...prev.lines[record.id], inspectionNotes: e.target.value } },
                  }))}
                  placeholder="Notes..."
                />
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        title={`Cancel ${po.poId}${po.vendor?.name ? ` · ${po.vendor.name}` : ''}`}
        open={cancelModalOpen}
        onOk={() => {
          if (!cancelReason.trim()) {
            message.warning('Please provide a reason for cancellation');
            return;
          }
          if (!cancelMrAction) {
            message.warning('Choose what happens to the material request');
            return;
          }
          handleStatusChange('Pending Cancellation', cancelReason.trim(), cancelMrAction);
        }}
        onCancel={() => {
          setCancelModalOpen(false);
          setCancelReason('');
          setCancelMrAction(null);
        }}
        confirmLoading={statusChanging}
        okText="Send to CEO"
        okButtonProps={{ danger: true, disabled: openLinesForCancel.length === 0 }}
        cancelText="Keep the order"
        width={680}
        destroyOnClose
      >
        <Text type="secondary">Goes to the CEO for approval. Nothing changes until approved. Every open quantity below is cancelled together; lines already fully received are left alone.</Text>
        <Table
          size="small"
          pagination={false}
          rowKey="id"
          style={{ margin: '12px 0' }}
          dataSource={po.lines || []}
          columns={[
            { title: 'Line', dataIndex: 'lineNo', width: 56 },
            { title: 'Item', key: 'item', render: (_: any, l: any) => l.mrLine?.catalogItem?.partNumber ? `${l.mrLine.catalogItem.partNumber} ${l.mrLine.catalogItem.description || l.itemDescription}` : l.itemDescription },
            { title: 'Ordered', key: 'ordered', width: 100, align: 'right' as const, render: (_: any, l: any) => `${Number(l.qty)} ${l.unit?.name || ''}` },
            { title: 'Received', dataIndex: 'totalReceived', width: 90, align: 'right' as const, render: (v: any) => Number(v || 0) },
            { title: 'To cancel', key: 'toCancel', width: 130, align: 'right' as const, render: (_: any, l: any) => openBalance(l) > 0 ? <strong>{openBalance(l)} {l.unit?.name || ''}</strong> : <span style={{ color: '#8a94a6' }}>{Number(l.cancelledQty || 0) > 0 ? 'already closed' : 'fully received, kept'}</span> },
          ]}
        />
        {openLinesForCancel.length === 0 && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Nothing is open on this PO. Every line is received or already cancelled." />
        )}
        <div style={{ marginBottom: 12 }}>
          <div><Text strong>Reason</Text> <Text type="danger">*</Text></div>
          <Input.TextArea
            rows={2}
            maxLength={500}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Why this order is not coming"
          />
        </div>
        <div style={{ marginBottom: 6 }}>
          <div><Text strong>What happens to the material request</Text> <Text type="danger">*</Text></div>
          <Radio.Group
            value={cancelMrAction}
            onChange={(e) => setCancelMrAction(e.target.value)}
            style={{ display: 'grid', gap: 8, marginTop: 6 }}
          >
            <Radio value="not_needed">
              <strong>No longer needed</strong>
              <div style={{ fontSize: 12, color: '#4b5563' }}>MR lines close at what was received. Lines with nothing received become Cancelled on the MR.</div>
            </Radio>
            <Radio value="reorder">
              <strong>Order it again</strong>
              <div style={{ fontSize: 12, color: '#4b5563' }}>The open quantities go back into Create PO, marked "back from {po.poId}", so they can be placed with another vendor.</div>
            </Radio>
          </Radio.Group>
          <Text type="secondary" style={{ fontSize: 12 }}>No default. If some lines should be re-ordered and others not, cancel those lines one by one instead.</Text>
        </div>
      </Modal>

      <Modal
        title={lineCancel ? `${Number(lineCancel.line.totalReceived || 0) > 0 ? 'Close balance on line' : 'Cancel line'} ${lineCancel.line.lineNo} · ${po.poId}` : ''}
        open={!!lineCancel}
        onOk={submitLineCancel}
        onCancel={() => setLineCancel(null)}
        confirmLoading={lineCancelBusy}
        okText="Send to CEO"
        okButtonProps={{ danger: true }}
        cancelText="Keep the line"
        width={620}
        destroyOnClose
      >
        {lineCancel && (() => {
          const l = lineCancel.line;
          const unit = l.unit?.name || '';
          const received = Number(l.totalReceived || 0);
          const max = openBalance(l);
          const mrId = l.mrLine?.mrHeader?.mrId;
          const price = Number(l.actualUnitPrice ?? l.unitPrice ?? 0);
          return (
            <div>
              <Text type="secondary">Goes to the CEO for approval. Nothing changes until approved.</Text>
              <div style={{ margin: '12px 0' }}>
                <ItemCell
                  partNumber={l.mrLine?.catalogItem?.partNumber || l.partNumber}
                  description={l.mrLine?.catalogItem?.description || l.itemDescription}
                  descriptionAr={l.mrLine?.catalogItem?.descriptionAr}
                />
              </div>
              <Descriptions size="small" bordered column={3} style={{ marginBottom: 14 }}>
                <Descriptions.Item label="Ordered">{Number(l.qty)} {unit}</Descriptions.Item>
                <Descriptions.Item label="Received">{received}</Descriptions.Item>
                <Descriptions.Item label="Open"><Text type="warning" strong>{max} {unit}</Text></Descriptions.Item>
              </Descriptions>
              <div style={{ marginBottom: 12 }}>
                <div><Text strong>Quantity to cancel</Text></div>
                <InputNumber
                  min={0}
                  max={max}
                  value={lineCancel.qty}
                  onChange={(v) => setLineCancel((p) => (p ? { ...p, qty: Number(v || 0) } : p))}
                  style={{ width: 120 }}
                />{' '}
                <Text type="secondary">{unit}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Up to {max}, the open quantity.{received > 0 ? ` The ${received} already received stays; to send it back, use a GRN return.` : ''}
                  </Text>
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <div><Text strong>Reason</Text> <Text type="danger">*</Text></div>
                <Input.TextArea
                  rows={2}
                  maxLength={500}
                  value={lineCancel.reason}
                  onChange={(e) => setLineCancel((p) => (p ? { ...p, reason: e.target.value } : p))}
                  placeholder="Why this quantity is not coming"
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div><Text strong>{mrId ? `${mrId} · line ${l.mrLine?.lineNo}` : 'MR line'}: what does the requester get?</Text></div>
                <Radio.Group
                  value={lineCancel.mrAction}
                  onChange={(e) => setLineCancel((p) => (p ? { ...p, mrAction: e.target.value } : p))}
                  style={{ display: 'grid', gap: 8, marginTop: 6 }}
                >
                  <Radio value="reorder">
                    <strong>Order it again</strong>
                    <div style={{ fontSize: 12, color: '#4b5563' }}>The {lineCancel.qty} {unit} go back to Create PO as approved and not ordered.</div>
                  </Radio>
                  <Radio value="not_needed">
                    <strong>No longer needed</strong>
                    <div style={{ fontSize: 12, color: '#4b5563' }}>
                      {received > 0
                        ? `The MR line closes as received, ${received} of ${Number(l.qty)}.`
                        : `The MR line closes as cancelled, 0 of ${Number(l.qty)} received.`}
                    </div>
                  </Radio>
                </Radio.Group>
              </div>
              <Alert
                type="info"
                message={
                  <span style={{ fontSize: 12 }}>
                    After approval: {lineCancel.qty} {unit} cancelled · {fmtNum(Math.round(lineCancel.qty * price * 100) / 100)} off the open value.
                    Receiving on line {l.lineNo} is locked until the CEO decides.
                  </span>
                }
              />
            </div>
          );
        })()}
      </Modal>

      <Modal
        title={lineDecision ? `Approve cancellation · line ${lineDecision.line.lineNo} · ${po.poId}` : ''}
        open={!!lineDecision}
        onOk={() => lineDecision && decideLine(lineDecision.line, 'approve', lineDecision.mrAction)}
        onCancel={() => setLineDecision(null)}
        confirmLoading={lineCancelBusy}
        okText="Approve cancellation"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        {lineDecision && (() => {
          const l = lineDecision.line;
          const unit = l.unit?.name || '';
          const received = Number(l.totalReceived || 0);
          return (
            <div>
              <p style={{ marginBottom: 6 }}>
                <strong>{Number(l.cancelRequestQty || 0)} {unit}</strong> of {Number(l.qty)} · {l.cancelReason}
              </p>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Requested by {l.cancelRequestedBy?.displayName || 'procurement'}
                {l.cancelRequestedAt ? ` · ${dayjs(l.cancelRequestedAt).format('DD MMM HH:mm')}` : ''}
              </Text>
              <div style={{ marginTop: 14 }}>
                <div><Text strong>What does the requester get?</Text></div>
                <Radio.Group
                  value={lineDecision.mrAction}
                  onChange={(e) => setLineDecision((p) => (p ? { ...p, mrAction: e.target.value } : p))}
                  style={{ display: 'grid', gap: 8, marginTop: 6 }}
                >
                  <Radio value="reorder"><strong>Order it again</strong> · back to Create PO</Radio>
                  <Radio value="not_needed">
                    <strong>No longer needed</strong> · {received > 0 ? `MR line closes at ${received} of ${Number(l.qty)}` : 'MR line cancelled'}
                  </Radio>
                </Radio.Group>
              </div>
            </div>
          );
        })()}
      </Modal>

    </div>
  );
};

export default POView;
